import React, { FC, memo } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import { useI18n, useLogger, useStaffCan } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import { TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const StaffSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');
  const staffCan = useStaffCan();

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Staff Settings)', { fields: data });

    commitToStorage(data);
  }

  const update = (data: Partial<StorageData>) => {
    logger.info('Updating Settings (Staff Settings)', { fields: { data } });

    updateStorage(data);
  };

  /** General staff settings unrelated to reports. */
  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.DisableStaffTimeout'),
      subLabel: i18n('Settings.DisableStaffTimeout.Text'),
      key: 'disableStaffTimeout',
      storage: storage.disableStaffTimeout,
    },
    {
      label: i18n('Settings.LogoutStaffAfterOrder'),
      subLabel: i18n('Settings.LogoutStaffAfterOrder.Text'),
      key: 'logoutStaffAfterOrder',
      storage: storage.logoutStaffAfterOrder,
    },
  ];

  /** Report-related staff settings, grouped into their own block. */
  const reportsTogglesConfig: Array<TogglesConfig> = [];

  if (staffCan.REPORTS) {
    reportsTogglesConfig.push({
      label: i18n('Settings.Reports.AllowStaffReport'),
      subLabel: i18n('Settings.Reports.AllowStaffReport.SubLabel'),
      key: 'staffCanViewOwnReport',
      storage: storage.staffCanViewOwnReport,
    });

    reportsTogglesConfig.push({
      label: i18n('Settings.Reports.CategoryBreakdown'),
      subLabel: i18n('Settings.Reports.CategoryBreakdown.SubLabel'),
      key: 'showDetailedSalesBreakdown',
      storage: storage.showDetailedSalesBreakdown,
    });
  }

  if (storageLoading) {
    return null;
  }

  /** Renders a toggle row from a toggle config entry. */
  const renderToggle = (toggle: TogglesConfig) =>
    !isHiddenOnDevice(toggle.hideIn, isHandy) && (
      <Toggle
        key={toggle.key}
        settingKey={toggle.key}
        label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
        description={toggle.subLabel}
        initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
        onToggle={() => update({ [toggle.key]: !toggle.storage })}
        fullWidth
        darkBackground
        lightText
        compact={isHandy}
      />
    );

  return (
    <Box
      padding={isHandy ? 'none' : 'large'}
      marginTop={isHandy ? 'small' : 'none'}
      display="flex"
      flexDirection="column"
      gap={isHandy ? 'small' : 'large'}>
      <SettingsBlock compact={isHandy}>{togglesConfig.map(renderToggle)}</SettingsBlock>

      {reportsTogglesConfig.length > 0 && (
        <SettingsBlock compact={isHandy} title={i18n('Settings.Reports')}>
          {reportsTogglesConfig.map(renderToggle)}
        </SettingsBlock>
      )}
    </Box>
  );
};

export default memo(StaffSettings);
